import { HttpErrorResponse } from "@angular/common/http";
import { EventEmitter, Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { MuxDemux, MuxDemuxDevicesModel, MuxDemuxInfoModel, MuxModel } from "../mux-demux.model";
import { NmsWizardStepMapData } from "@nms-angular-toolkit/nms-wizard";
import { ANGULARJS_TRANSLATE, ANGULARJS_ROOTSCOPE, NMS_STATES, STATE, AUTHENTICATION_SERVICE }
    from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { NmsToastrLinkService } from "@nms-ng2/app/shared/components/elements/nms-toastr/nms-toastr-link.service";
import { NmsToasterLink } from "@nms-ng2/app/shared/components/elements/nms-toastr/nms-toastr-model";
import { ErrorResponseHandlerService } from "@nms-ng2/app/core/services/error-response-handler/error-response-handler.service";
import { NmsDialogService } from "@nms-angular-toolkit/nms-dialog";
import { MuxRestService } from "@nms-ng2/app/shared/services/inventory/mux/mux-rest.service";
import { ERROR_NAME } from "@nms-ng2/app/core/services/error-response-handler/error-names.constant";

/**
 * Serviço responsável por realizar as requisições referentes aos Mux/Demux.
 */
@Injectable({
    providedIn: "root"
})
export class MuxDemuxService {
    private readonly baseUrl: string;
    private readonly CLOSE: boolean = true;
    private readonly RELOAD: boolean = true;
    closeModalEmmiter = new EventEmitter<boolean>();
    reloadListEmmiter = new EventEmitter<boolean>();

    constructor(private readonly nmsToastrLinkService: NmsToastrLinkService,
                private readonly errorResponseHandler: ErrorResponseHandlerService,
                private readonly nmsDialogService: NmsDialogService, private readonly muxService: MuxRestService,
                @Inject(ANGULARJS_TRANSLATE) private readonly translate, @Inject(NMS_STATES) private readonly nmsStates: any,
                @Inject(ANGULARJS_ROOTSCOPE) private readonly $rootScope: any, @Inject(STATE) private readonly $state: any,
                @Inject(AUTHENTICATION_SERVICE) private readonly authenticationService: any) {
        this.baseUrl = "/mux";
    }

    getMuxModels(): Observable<Array<MuxModel>> {
        const muxModels = this.muxService.getMuxModels();

        muxModels.subscribe({
            error: (error: HttpErrorResponse) => this.showErrorMuxDemuxMessage(error)
        });

        return muxModels;
    }

    async getMux(resourceId: number): Promise<MuxDemux> {
        try {
            return await this.muxService.getMux(resourceId).toPromise();
        } catch (error) {
            this.showErrorMuxDemuxMessage(error);
            return Promise.reject();
        }
    }

    async saveMuxDemux(muxDemux: MuxDemux): Promise<void> {
        try {
            await this.muxService.saveMuxDemux(muxDemux).toPromise();

            this.showSuccessMuxDemuxMessage("mux.demux.modal.save.successfully", this.createSuccessDetailsLink());

            return Promise.resolve();
        } catch (error) {
            const message: string = this.translate.instant("add.mux.demux.modal.error.add") + "<br>";
            this.showErrorMuxDemuxMessage(error, message);

            return Promise.reject();
        }
    }

    async updateMuxDemux(muxDemux: MuxDemux): Promise<void> {
        try {
            await this.muxService.updateMuxDemux(muxDemux).toPromise();

            this.showSuccessMuxDemuxMessage("mux.demux.modal.edited.successfully", this.createSuccessDetailsLink());

            return Promise.resolve();
        } catch (error) {
            const message: string = this.translate.instant("edit.mux.demux.modal.error.edit") + "<br>";
            this.showErrorMuxDemuxMessage(error, message);

            return Promise.reject();
        }
    }

    async removeMuxDemux(muxDemux: MuxDemux[]) {
        const isOneMuxSelected = muxDemux.length === 1;
        try {
            const removeResult = await this.muxService.removeMuxDemux(muxDemux).toPromise();

            if (removeResult.false.length === 0) {
                const message = isOneMuxSelected
                    ? this.translate.instant("mux.table.remove.single.successfully")
                    : this.translate.instant("mux.table.remove.plural.successfully").replace("{0}", muxDemux.length);
                this.nmsToastrLinkService.showToastSuccess(message, null);
                this.reloadListEmmiter.emit(this.RELOAD);
            } else {
                this.showMessageWithPartialRemovedMuxes(removeResult.true, removeResult.false, muxDemux);
            }

        } catch (error) {
            const errorResponse = error as HttpErrorResponse;
            const { code, details } = errorResponse.error;
            let prefix = "mux.demux.modal.remove.single.error.";
            let detailsMessage = "";

            if (!isOneMuxSelected) {
                prefix = "mux.demux.modal.remove.multiple.error.";
                detailsMessage = this.errorResponseHandler.buildErrorDetails(code, details);
            }

            const translatedMessage = this.translate.instant(`${prefix}${ERROR_NAME[code]}`);
            this.showMessage(`${translatedMessage}${detailsMessage}`);
        }
    }

    buildMuxDemux(wizardStepsCacheMap: NmsWizardStepMapData): MuxDemux {
        const muxDemux: MuxDemux = new MuxDemux();
        const muxDemuxInfoModel = wizardStepsCacheMap["MuxDemuxInfoModel"] as MuxDemuxInfoModel;
        const muxDemuxDevicesModel = wizardStepsCacheMap["MuxDemuxDevicesModel"] as MuxDemuxDevicesModel;

        muxDemux.resourceId = muxDemuxInfoModel.resourceId;
        muxDemux.name = muxDemuxInfoModel.name || "";
        muxDemux.serialNumber = muxDemuxInfoModel.serialNumber || "";
        muxDemux.model = muxDemuxInfoModel.model || "";
        muxDemux.devices = muxDemuxDevicesModel.devices || [];
        muxDemux.description = muxDemuxDevicesModel.description || "";

        return muxDemux;
    }

    buildCacheMap(mux: MuxDemux): NmsWizardStepMapData {
        const muxDemuxInfoModel = new MuxDemuxInfoModel();
        const muxDemuxDevicesModel = new MuxDemuxDevicesModel();
        muxDemuxInfoModel.resourceId = mux.resourceId;
        muxDemuxInfoModel.model = mux.model;
        muxDemuxInfoModel.name = mux.name;
        muxDemuxInfoModel.serialNumber = mux.serialNumber;
        muxDemuxDevicesModel.devices = mux.devices;
        muxDemuxDevicesModel.description = mux.description;

        return { MuxDemuxInfoModel: muxDemuxInfoModel, MuxDemuxDevicesModel: muxDemuxDevicesModel };
    }

    private showSuccessMuxDemuxMessage(prefix: string, messageLinks?: NmsToasterLink[]) {
        this.reloadListEmmiter.emit(this.RELOAD);
        const message: string = this.translate.instant(prefix);

        this.nmsToastrLinkService.showToastSuccess(message, messageLinks);
        this.closeModalEmmiter.emit(this.CLOSE);
    }

    private createSuccessDetailsLink(): NmsToasterLink[] {
        const link = {
            id: "mux-demux-id",
            title: this.translate.instant("mux.demux.modal.see.mux.demux"),
            action: () => this.$state.go(this.nmsStates.dwdmMuxDemux, {}, { reload: true })
        };

        return [link];
    }

    private buildErrorMessage(error: HttpErrorResponse, message = "") {
        const { description, details } = this.errorResponseHandler.buildErrorDescriptionDetails(error.error);

        return `${message}${description}${details}`;
    }

    private showErrorMuxDemuxMessage(error: HttpErrorResponse, message = "") {
        this.showMessage(this.buildErrorMessage(error, message));
    }

    private showMessage(message: string, reloadList: boolean = false) {
        this.nmsDialogService.openDialog({ description: message, acceptLabel: "OK", bindHtml: true }, { maxWidth: "800px" })
            .catch(() => {
                if (reloadList) {
                    this.reloadListEmmiter.emit(this.RELOAD);
                }
            });
    }

    private showMessageWithPartialRemovedMuxes(
        removedMuxesResourceIds: Array<number>, notFoundMuxesResourceIds: Array<number>, muxDemux: MuxDemux[]) {
        const notFoundMuxesNames = notFoundMuxesResourceIds
            .map(muxResourceId => muxDemux.find(mux => mux.resourceId === muxResourceId).name).join("<br>");
        const notRemovedMessage = this.translate.instant("mux.demux.modal.already.remove.devices")
            .replace("{0}", notFoundMuxesNames);
        if (removedMuxesResourceIds.length === 0) {
            this.showMessage(notRemovedMessage, true);
        } else {
            const totalMuxesRemoved = muxDemux.length - notFoundMuxesResourceIds.length;
            const removedSuccessMessage = removedMuxesResourceIds.length > 1
                ? this.translate.instant("mux.table.remove.plural.successfully").replace("{0}", totalMuxesRemoved)
                : this.translate.instant("mux.table.remove.single.successfully");
            this.showMessage(`${removedSuccessMessage} ${notRemovedMessage}`, true);
        }
    }

    public userHasPermission(permission: string): boolean {
        const hasPermission = this.authenticationService.hasPermission(permission);

        if (!hasPermission) {
            this.nmsDialogService.openDialog({
                description: this.translate.instant("mux.demux.error.noUserPermission"),
                acceptLabel: "OK"
            });
            return false;
        }

        return true;
    }
}
